import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import {
  Button,
  Menu,
  MenuButton,
  MenuContent,
  MenuList,
  Tooltip,
} from '@apollo/orbit';
import React from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { PermissionWarningContents } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { useGrowthShowRoverCTA } from 'src/lib/featureFlags';
import { UserPermission } from 'src/lib/graphqlTypes/types';

import { GraphCreationOptions } from './GraphCreationOptions';

/**
 * GraphCreationButton
 *
 * Renders a button for creating new graphs, with behavior and appearance
 * that varies based on user permissions, plan type, feature flags, and available capabilities.
 *
 * Logic:
 * 1. Permission-Based Rendering:
 *    - If user lacks 'canCreateService' permission, the button is disabled with a tooltip
 *      explaining why.
 * 2. Button Type Selection:
 *    - When menu items are available: Uses the Menu component with MenuButton
 *    - When no menu items are available: Uses a regular Button component that links directly
 *      to creating a graph with a GraphQL API
 * 3. Menu Items Logic:
 *    - If hasConnectorsCapabilities is true:
 *      - When `show-rover-init-cta` flag is ON: Shows menu items for rover init and graphql api connection
 *      - When `show-rover-init-cta` flag is OFF: Shows "Connect REST API" menu item
 *    - If hasConnectorsCapabilities is false but hasCloudGraphsCapability is true:
 *      - Shows "Try a demo graph" menu item
 *    - If neither capability is present:
 *      - No menu items are shown, a Button is rendered instead
 * 4. Button Destination Logic:
 *    - For Cloud-Only Plans (Dedicated or Serverless): Links to the flow for cloud graph creation
 */
export const GraphCreationButton = ({
  canCreateService,
  hasConnectorsCapabilities,
  hasCloudGraphsCapability,
  accountRole,
  accountId,
  isCloudOnlyPlan,
}: {
  canCreateService: boolean | undefined;
  hasConnectorsCapabilities: boolean | undefined;
  hasCloudGraphsCapability: boolean | undefined;
  accountRole: UserPermission | undefined;
  accountId: string;
  isCloudOnlyPlan: boolean;
}) => {
  const showRoverInitElements = useGrowthShowRoverCTA();
  const { menuItems, defaultAction } = GraphCreationOptions({
    accountId,
    hasConnectorsCapabilities,
    hasCloudGraphsCapability,
    isCloudOnlyPlan,
    showRoverInitElements,
  });
  const menuContentProps = {
    ...(showRoverInitElements && {
      key: 'rover-init',
      className: 'max-w-72 shadow-md',
    }),
  };

  // When no extra menu items are available, render a direct action button
  if (!menuItems) {
    return (
      <Button
        as={ReactRouterLink}
        to={defaultAction.to}
        onClick={defaultAction.onClick}
        variant="primary"
        data-testid="create-graph-button"
      >
        Add new graph
      </Button>
    );
  }

  // For disabled state - when user lacks permission to create a service
  if (!canCreateService) {
    return (
      <Tooltip
        placement="left"
        label={<PermissionWarningContents accountRole={accountRole} />}
      >
        <span>
          <Button
            data-testid="create-graph-button"
            variant="primary"
            size="md"
            isDisabled={true}
          >
            Add new graph
          </Button>
        </span>
      </Tooltip>
    );
  }

  // When enabled, render Menu with MenuButton
  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        isDisabled={!canCreateService}
        rightIcon={<IconChevronDown />}
        variant="primary"
        data-testid="create-graph-button"
      >
        Add new graph
      </MenuButton>
      <MenuContent {...menuContentProps}>
        <MenuList>{menuItems}</MenuList>
      </MenuContent>
    </Menu>
  );
};
